/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Rocket.net API
 * Rocket.net - All APIs
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { customFetch } from '../../custom-fetch';
import { addMetaToMutation } from '../../mutators/add-meta';
import { addMetaToQuery } from '../../mutators/add-meta';
import type {
  CreateAccountRequest,
  CreateAccountResponse,
  CreatePartnerAIBuilderSessionRequest,
  CreatePartnerAIBuilderSessionResponse,
  CreatePartnerAccountHostingPlanRequest,
  CreatePartnerAccountHostingPlanResponse,
  CreatePartnerSiteRequest,
  CreateSiteResponse,
  Error,
  ListPartnerProductsParams,
  PartnerBillingProductsResponse,
  SiteID,
  SiteSuspendResponse,
  SiteUnsuspendResponse,
  UpdatePartnerSiteRequest,
  UpdatePartnerSiteResponse,
} from '../rocketNetAPI.schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Creates a new Rocket.net account on behalf of a customer. Returns API token and account ID.
 * @summary Provision a customer account
 */
export const createPartnerAccount = (
  createAccountRequest: CreateAccountRequest,
  signal?: AbortSignal
) => {
  return customFetch<CreateAccountResponse>({
    url: `/v1/partner/accounts`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createAccountRequest,
    signal,
  });
};

export const getCreatePartnerAccountMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPartnerAccount>>,
    TError,
    { data: CreateAccountRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPartnerAccount>>,
  TError,
  { data: CreateAccountRequest },
  TContext
> => {
  const mutationKey = ['createPartnerAccount'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPartnerAccount>>,
    { data: CreateAccountRequest }
  > = props => {
    const { data } = props ?? {};

    return createPartnerAccount(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/partner/accounts` },
    {
      operationId: 'app.controllers.partner_controller.create_partner_account',
      operationName: 'createPartnerAccount',
    }
  );

  return customOptions;
};

export type CreatePartnerAccountMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPartnerAccount>>
>;
export type CreatePartnerAccountMutationBody = CreateAccountRequest;
export type CreatePartnerAccountMutationError = Error;

/**
 * @summary Provision a customer account
 */
export const useCreatePartnerAccount = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPartnerAccount>>,
      TError,
      { data: CreateAccountRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createPartnerAccount>>,
  TError,
  { data: CreateAccountRequest },
  TContext
> => {
  const mutationOptions = getCreatePartnerAccountMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Assigns a hosting plan to a customer account you manage. Partner billing; no customer checkout step.
 * @summary Assign a hosting plan to a customer account
 */
export const createPartnerAccountHostingPlan = (
  id: number,
  createPartnerAccountHostingPlanRequest: CreatePartnerAccountHostingPlanRequest,
  signal?: AbortSignal
) => {
  return customFetch<CreatePartnerAccountHostingPlanResponse>({
    url: `/v1/partner/accounts/${id}/hosting_plan`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createPartnerAccountHostingPlanRequest,
    signal,
  });
};

export const getCreatePartnerAccountHostingPlanMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPartnerAccountHostingPlan>>,
    TError,
    { id: number; data: CreatePartnerAccountHostingPlanRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPartnerAccountHostingPlan>>,
  TError,
  { id: number; data: CreatePartnerAccountHostingPlanRequest },
  TContext
> => {
  const mutationKey = ['createPartnerAccountHostingPlan'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPartnerAccountHostingPlan>>,
    { id: number; data: CreatePartnerAccountHostingPlanRequest }
  > = props => {
    const { id, data } = props ?? {};

    return createPartnerAccountHostingPlan(id, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/partner/accounts/{id}/hosting_plan` },
    {
      operationId: 'app.controllers.partner_controller.create_partner_account_hosting_plan',
      operationName: 'createPartnerAccountHostingPlan',
    }
  );

  return customOptions;
};

export type CreatePartnerAccountHostingPlanMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPartnerAccountHostingPlan>>
>;
export type CreatePartnerAccountHostingPlanMutationBody = CreatePartnerAccountHostingPlanRequest;
export type CreatePartnerAccountHostingPlanMutationError = Error;

/**
 * @summary Assign a hosting plan to a customer account
 */
export const useCreatePartnerAccountHostingPlan = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPartnerAccountHostingPlan>>,
      TError,
      { id: number; data: CreatePartnerAccountHostingPlanRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createPartnerAccountHostingPlan>>,
  TError,
  { id: number; data: CreatePartnerAccountHostingPlanRequest },
  TContext
> => {
  const mutationOptions = getCreatePartnerAccountHostingPlanMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Lists hosting plans available for partner provisioning, including AI Builder plans.
 * @summary List selectable hosting plans
 */
export const listPartnerProducts = (params?: ListPartnerProductsParams, signal?: AbortSignal) => {
  return customFetch<PartnerBillingProductsResponse>({
    url: `/v1/partner/products`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListPartnerProductsQueryKey = (params?: ListPartnerProductsParams) => {
  return [`/v1/partner/products`, ...(params ? [params] : [])] as const;
};

export const getListPartnerProductsQueryOptions = <
  TData = Awaited<ReturnType<typeof listPartnerProducts>>,
  TError = Error,
>(
  params?: ListPartnerProductsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListPartnerProductsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listPartnerProducts>>> = ({ signal }) =>
    listPartnerProducts(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/partner/products`,
      operationId: 'app.controllers.partner_controller.list_partner_products',
      operationName: 'listPartnerProducts',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof listPartnerProducts>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListPartnerProductsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listPartnerProducts>>
>;
export type ListPartnerProductsQueryError = Error;

export function useListPartnerProducts<
  TData = Awaited<ReturnType<typeof listPartnerProducts>>,
  TError = Error,
>(
  params: undefined | ListPartnerProductsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPartnerProducts>>,
          TError,
          Awaited<ReturnType<typeof listPartnerProducts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPartnerProducts<
  TData = Awaited<ReturnType<typeof listPartnerProducts>>,
  TError = Error,
>(
  params?: ListPartnerProductsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPartnerProducts>>,
          TError,
          Awaited<ReturnType<typeof listPartnerProducts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPartnerProducts<
  TData = Awaited<ReturnType<typeof listPartnerProducts>>,
  TError = Error,
>(
  params?: ListPartnerProductsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List selectable hosting plans
 */

export function useListPartnerProducts<
  TData = Awaited<ReturnType<typeof listPartnerProducts>>,
  TError = Error,
>(
  params?: ListPartnerProductsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListPartnerProductsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getListPartnerProductsSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof listPartnerProducts>>,
  TError = Error,
>(
  params?: ListPartnerProductsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListPartnerProductsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listPartnerProducts>>> = ({ signal }) =>
    listPartnerProducts(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/partner/products`,
      operationId: 'app.controllers.partner_controller.list_partner_products',
      operationName: 'listPartnerProducts',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof listPartnerProducts>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListPartnerProductsSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof listPartnerProducts>>
>;
export type ListPartnerProductsSuspenseQueryError = Error;

export function useListPartnerProductsSuspense<
  TData = Awaited<ReturnType<typeof listPartnerProducts>>,
  TError = Error,
>(
  params: undefined | ListPartnerProductsParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPartnerProductsSuspense<
  TData = Awaited<ReturnType<typeof listPartnerProducts>>,
  TError = Error,
>(
  params?: ListPartnerProductsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPartnerProductsSuspense<
  TData = Awaited<ReturnType<typeof listPartnerProducts>>,
  TError = Error,
>(
  params?: ListPartnerProductsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List selectable hosting plans
 */

export function useListPartnerProductsSuspense<
  TData = Awaited<ReturnType<typeof listPartnerProducts>>,
  TError = Error,
>(
  params?: ListPartnerProductsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListPartnerProductsSuspenseQueryOptions(params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates a WordPress, static, or AI Builder site with optional per-site disk and bandwidth limits.
 * @summary Create a customer site
 */
export const createPartnerSite = (
  createPartnerSiteRequest: CreatePartnerSiteRequest,
  signal?: AbortSignal
) => {
  return customFetch<CreateSiteResponse>({
    url: `/v1/partner/sites`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createPartnerSiteRequest,
    signal,
  });
};

export const getCreatePartnerSiteMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPartnerSite>>,
    TError,
    { data: CreatePartnerSiteRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPartnerSite>>,
  TError,
  { data: CreatePartnerSiteRequest },
  TContext
> => {
  const mutationKey = ['createPartnerSite'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPartnerSite>>,
    { data: CreatePartnerSiteRequest }
  > = props => {
    const { data } = props ?? {};

    return createPartnerSite(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/partner/sites` },
    {
      operationId: 'app.controllers.partner_controller.create_partner_site',
      operationName: 'createPartnerSite',
    }
  );

  return customOptions;
};

export type CreatePartnerSiteMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPartnerSite>>
>;
export type CreatePartnerSiteMutationBody = CreatePartnerSiteRequest;
export type CreatePartnerSiteMutationError = Error;

/**
 * @summary Create a customer site
 */
export const useCreatePartnerSite = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPartnerSite>>,
      TError,
      { data: CreatePartnerSiteRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createPartnerSite>>,
  TError,
  { data: CreatePartnerSiteRequest },
  TContext
> => {
  const mutationOptions = getCreatePartnerSiteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Updates partner-managed settings: disk quota, bandwidth limit, label, and metadata.
 * @summary Update site limits
 */
export const modifyPartnerSite = (
  id: SiteID,
  updatePartnerSiteRequest: UpdatePartnerSiteRequest
) => {
  return customFetch<UpdatePartnerSiteResponse>({
    url: `/v1/partner/sites/${id}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updatePartnerSiteRequest,
  });
};

export const getModifyPartnerSiteMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof modifyPartnerSite>>,
    TError,
    { id: SiteID; data: UpdatePartnerSiteRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof modifyPartnerSite>>,
  TError,
  { id: SiteID; data: UpdatePartnerSiteRequest },
  TContext
> => {
  const mutationKey = ['modifyPartnerSite'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof modifyPartnerSite>>,
    { id: SiteID; data: UpdatePartnerSiteRequest }
  > = props => {
    const { id, data } = props ?? {};

    return modifyPartnerSite(id, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/partner/sites/{id}` },
    {
      operationId: 'app.controllers.partner_controller.modify_partner_site',
      operationName: 'modifyPartnerSite',
    }
  );

  return customOptions;
};

export type ModifyPartnerSiteMutationResult = NonNullable<
  Awaited<ReturnType<typeof modifyPartnerSite>>
>;
export type ModifyPartnerSiteMutationBody = UpdatePartnerSiteRequest;
export type ModifyPartnerSiteMutationError = Error;

/**
 * @summary Update site limits
 */
export const useModifyPartnerSite = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof modifyPartnerSite>>,
      TError,
      { id: SiteID; data: UpdatePartnerSiteRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof modifyPartnerSite>>,
  TError,
  { id: SiteID; data: UpdatePartnerSiteRequest },
  TContext
> => {
  const mutationOptions = getModifyPartnerSiteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Creates an AI Builder session for a site and returns the session URL for the end user.
 * @summary Start AI site builder session
 */
export const createPartnerSiteAiBuilderSession = (
  id: SiteID,
  createPartnerAIBuilderSessionRequest: CreatePartnerAIBuilderSessionRequest,
  signal?: AbortSignal
) => {
  return customFetch<CreatePartnerAIBuilderSessionResponse>({
    url: `/v1/partner/sites/${id}/ai_builder/session`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createPartnerAIBuilderSessionRequest,
    signal,
  });
};

export const getCreatePartnerSiteAiBuilderSessionMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPartnerSiteAiBuilderSession>>,
    TError,
    { id: SiteID; data: CreatePartnerAIBuilderSessionRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPartnerSiteAiBuilderSession>>,
  TError,
  { id: SiteID; data: CreatePartnerAIBuilderSessionRequest },
  TContext
> => {
  const mutationKey = ['createPartnerSiteAiBuilderSession'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPartnerSiteAiBuilderSession>>,
    { id: SiteID; data: CreatePartnerAIBuilderSessionRequest }
  > = props => {
    const { id, data } = props ?? {};

    return createPartnerSiteAiBuilderSession(id, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/partner/sites/{id}/ai_builder/session` },
    {
      operationId: 'app.controllers.partner_controller.create_partner_site_ai_builder_session',
      operationName: 'createPartnerSiteAiBuilderSession',
    }
  );

  return customOptions;
};

export type CreatePartnerSiteAiBuilderSessionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPartnerSiteAiBuilderSession>>
>;
export type CreatePartnerSiteAiBuilderSessionMutationBody = CreatePartnerAIBuilderSessionRequest;
export type CreatePartnerSiteAiBuilderSessionMutationError = Error;

/**
 * @summary Start AI site builder session
 */
export const useCreatePartnerSiteAiBuilderSession = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPartnerSiteAiBuilderSession>>,
      TError,
      { id: SiteID; data: CreatePartnerAIBuilderSessionRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createPartnerSiteAiBuilderSession>>,
  TError,
  { id: SiteID; data: CreatePartnerAIBuilderSessionRequest },
  TContext
> => {
  const mutationOptions = getCreatePartnerSiteAiBuilderSessionMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Takes a production site offline and clears its cache.
 * @summary Suspend a site
 */
export const suspendPartnerSite = (id: SiteID, signal?: AbortSignal) => {
  return customFetch<SiteSuspendResponse>({
    url: `/v1/partner/sites/${id}/suspend`,
    method: 'POST',
    signal,
  });
};

export const getSuspendPartnerSiteMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof suspendPartnerSite>>,
    TError,
    { id: SiteID },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof suspendPartnerSite>>,
  TError,
  { id: SiteID },
  TContext
> => {
  const mutationKey = ['suspendPartnerSite'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof suspendPartnerSite>>,
    { id: SiteID }
  > = props => {
    const { id } = props ?? {};

    return suspendPartnerSite(id);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/partner/sites/{id}/suspend` },
    {
      operationId: 'app.controllers.partner_controller.suspend_partner_site',
      operationName: 'suspendPartnerSite',
    }
  );

  return customOptions;
};

export type SuspendPartnerSiteMutationResult = NonNullable<
  Awaited<ReturnType<typeof suspendPartnerSite>>
>;

export type SuspendPartnerSiteMutationError = Error;

/**
 * @summary Suspend a site
 */
export const useSuspendPartnerSite = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof suspendPartnerSite>>,
      TError,
      { id: SiteID },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof suspendPartnerSite>>,
  TError,
  { id: SiteID },
  TContext
> => {
  const mutationOptions = getSuspendPartnerSiteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Brings a suspended production site back online and clears its cache.
 * @summary Restore a suspended site
 */
export const unsuspendPartnerSite = (id: SiteID, signal?: AbortSignal) => {
  return customFetch<SiteUnsuspendResponse>({
    url: `/v1/partner/sites/${id}/unsuspend`,
    method: 'POST',
    signal,
  });
};

export const getUnsuspendPartnerSiteMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof unsuspendPartnerSite>>,
    TError,
    { id: SiteID },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof unsuspendPartnerSite>>,
  TError,
  { id: SiteID },
  TContext
> => {
  const mutationKey = ['unsuspendPartnerSite'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof unsuspendPartnerSite>>,
    { id: SiteID }
  > = props => {
    const { id } = props ?? {};

    return unsuspendPartnerSite(id);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/partner/sites/{id}/unsuspend` },
    {
      operationId: 'app.controllers.partner_controller.unsuspend_partner_site',
      operationName: 'unsuspendPartnerSite',
    }
  );

  return customOptions;
};

export type UnsuspendPartnerSiteMutationResult = NonNullable<
  Awaited<ReturnType<typeof unsuspendPartnerSite>>
>;

export type UnsuspendPartnerSiteMutationError = Error;

/**
 * @summary Restore a suspended site
 */
export const useUnsuspendPartnerSite = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof unsuspendPartnerSite>>,
      TError,
      { id: SiteID },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof unsuspendPartnerSite>>,
  TError,
  { id: SiteID },
  TContext
> => {
  const mutationOptions = getUnsuspendPartnerSiteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
